// src/components/resources/calculators/FullFunnelCalculator.logic.ts

/**
 * Pure, framework-free Full-Funnel calculator math — extracted from
 * `FullFunnelCalculator.tsx` so it can be unit-tested directly (mirrors the
 * `MerCalculator.logic.ts` / `ROASCalculator.logic.ts` convention). All
 * arithmetic, the deterministic recommendation set, and input validation live
 * here; the component only renders.
 *
 * The model walks a paid-media funnel end to end:
 *
 *   ad spend      = CPM × impressions / 1,000
 *   clicks        = impressions × CTR%
 *   conversions   = clicks × CVR%
 *   new customers = conversions × new-customer-rate%
 *   revenue       = conversions × AOV
 *
 * and derives the four headline outputs plus an LTV:CAC health ratio:
 *
 *   ROAS    = revenue / ad spend
 *   CPA     = ad spend / conversions          (cost per conversion)
 *   CAC     = ad spend / new customers        (cost per *new* customer)
 *   LTV     = AOV × orders-per-customer × gross-margin%   (contribution basis)
 *   LTV:CAC = LTV / CAC
 *
 * Division-by-zero is intentionally left as `Infinity` / `NaN` (not coerced) so
 * the component can render `∞` / `N/A` and the validation layer can warn — the
 * same edge-handling contract the sibling calculators use.
 */

export interface FunnelInputs {
  /** Total impressions delivered. */
  impressions: number
  /** Cost per 1,000 impressions, in dollars. */
  cpm: number
  /** Click-through rate, as a percentage (e.g. 1.2 = 1.2%). */
  ctr: number
  /** Conversion rate of clicks, as a percentage (e.g. 3 = 3%). */
  cvr: number
  /** Average order value, in dollars. */
  aov: number
  /** Share of conversions that are *new* customers, as a percentage. */
  newCustomerRate: number
  /** Average lifetime orders per customer (drives LTV). */
  ordersPerCustomer: number
  /** Gross margin, as a percentage (contribution basis for LTV). */
  grossMargin: number
}

export interface FunnelRecommendation {
  type: 'primary' | 'optimization' | 'tip'
  text: string
  icon?: 'chart' | 'acquisition' | 'money' | 'strategy' | 'warning'
}

export interface FunnelResults {
  // Derived funnel volumes
  adSpend: number
  clicks: number
  conversions: number
  newCustomers: number
  revenue: number
  // Headline metrics
  roas: number
  cpa: number
  cac: number
  ltv: number
  ltvToCac: number
  recommendations: FunnelRecommendation[]
}

/**
 * Computes the full funnel — derived volumes, the four headline metrics, the
 * LTV:CAC ratio, and a deterministic recommendation set.
 */
export function calculateFunnel(inputs: FunnelInputs): FunnelResults {
  const adSpend = (inputs.cpm * inputs.impressions) / 1000
  const clicks = inputs.impressions * (inputs.ctr / 100)
  const conversions = clicks * (inputs.cvr / 100)
  const newCustomers = conversions * (inputs.newCustomerRate / 100)
  const revenue = conversions * inputs.aov

  const roas = revenue / adSpend
  const cpa = adSpend / conversions
  const cac = adSpend / newCustomers
  const ltv = inputs.aov * inputs.ordersPerCustomer * (inputs.grossMargin / 100)
  // Guard the zero-acquisition case: with no new customers `cac` is Infinity,
  // and `ltv / Infinity` would be a *finite* 0 that reads as a real (terrible)
  // ratio. Surface it as NaN so the UI shows "N/A" instead of "0.0:1".
  const ltvToCac = cac > 0 && isFinite(cac) ? ltv / cac : NaN

  return {
    adSpend,
    clicks,
    conversions,
    newCustomers,
    revenue,
    roas,
    cpa,
    cac,
    ltv,
    ltvToCac,
    recommendations: buildRecommendations({
      roas,
      cpa,
      ltvToCac,
      aov: inputs.aov,
      grossMargin: inputs.grossMargin,
    }),
  }
}

/**
 * Deterministic insights derived from the metric relationships. Every branch
 * guards `isFinite` so divide-by-zero states never produce a misleading tip.
 */
function buildRecommendations(m: {
  roas: number
  cpa: number
  ltvToCac: number
  aov: number
  grossMargin: number
}): FunnelRecommendation[] {
  const recommendations: FunnelRecommendation[] = []

  if (isFinite(m.roas)) {
    recommendations.push({
      type: 'primary',
      icon: 'chart',
      text: `At these funnel rates your blended ROAS is ${m.roas.toFixed(2)}x — $${m.roas.toFixed(2)} of revenue for every $1 of media spend.`,
    })
  }

  // LTV:CAC — the unit-economics health signal (3:1 is the common target).
  if (isFinite(m.ltvToCac)) {
    if (m.ltvToCac >= 3) {
      recommendations.push({
        type: 'tip',
        icon: 'money',
        text: `Your LTV:CAC is ${m.ltvToCac.toFixed(1)}:1, at or above the 3:1 rule of thumb — healthy unit economics with room to scale acquisition.`,
      })
    } else if (m.ltvToCac >= 1) {
      recommendations.push({
        type: 'optimization',
        icon: 'strategy',
        text: `Your LTV:CAC is ${m.ltvToCac.toFixed(1)}:1 — profitable but below the 3:1 target. Lift repeat purchases, margin, or conversion rate before scaling spend.`,
      })
    } else {
      recommendations.push({
        type: 'optimization',
        icon: 'warning',
        text: `Your LTV:CAC is ${m.ltvToCac.toFixed(1)}:1 — you're spending more to acquire a customer than they're worth. Fix conversion rate, AOV, or retention before adding budget.`,
      })
    }
  }

  // CPA vs first-order *contribution* (AOV × gross margin), not gross AOV —
  // LTV is contribution-based, so a first order can lose money on margin even
  // when CPA < AOV. This flags the loss that repeat revenue must cover.
  const firstOrderContribution = m.aov * (m.grossMargin / 100)
  if (isFinite(m.cpa) && firstOrderContribution > 0 && m.cpa > firstOrderContribution) {
    recommendations.push({
      type: 'optimization',
      icon: 'acquisition',
      text: `Your CPA (${formatMoney(m.cpa)}) exceeds your first-order contribution profit (AOV × margin = ${formatMoney(firstOrderContribution)}), so the first order loses money — you're relying on repeat orders (LTV) to turn a profit.`,
    })
  }

  return recommendations
}

/** Compact USD formatter for recommendation strings (no external dep). */
function formatMoney(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

/**
 * Validates inputs for logical consistency. Returns warnings (never blocks the
 * calculation — the component renders results *and* the warnings, matching MER).
 */
export function validateFunnelInputs(inputs: FunnelInputs): Array<{ type: 'warning'; message: string }> {
  const warnings: Array<{ type: 'warning'; message: string }> = []

  if (inputs.impressions <= 0 || inputs.cpm <= 0) {
    warnings.push({
      type: 'warning',
      message: 'Impressions and CPM must both be greater than zero, otherwise ad spend and the downstream metrics are undefined.',
    })
  }
  if (inputs.ctr > 100) {
    warnings.push({ type: 'warning', message: 'Click-through rate cannot exceed 100% — please check your CTR input.' })
  }
  if (inputs.cvr > 100) {
    warnings.push({ type: 'warning', message: 'Conversion rate cannot exceed 100% — please check your CVR input.' })
  }
  if (inputs.newCustomerRate > 100) {
    warnings.push({ type: 'warning', message: 'New-customer share cannot exceed 100% of conversions — please check that input.' })
  }
  if (inputs.grossMargin > 100) {
    warnings.push({ type: 'warning', message: 'Gross margin cannot exceed 100% — please check your margin input.' })
  }

  return warnings
}
